"use client";

import { c as _c } from "react/compiler-runtime";
import { Bar, BarChart as RechartsBarChart, Line, LineChart as RechartsLineChart, Area, AreaChart as RechartsAreaChart, Pie, PieChart as RechartsPieChart, Radar, RadarChart as RechartsRadarChart, RadialBar, RadialBarChart as RechartsRadialBarChart, XAxis, YAxis, CartesianGrid, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Label } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent, type ChartConfig } from "@/components/ui/chart";
const CHART_COLORS = ["var(--chart-1)", "var(--chart-2)", "var(--chart-3)", "var(--chart-4)", "var(--chart-5)"];
interface Series {
  dataKey: string;
  label: string;
  color?: string;
  stackId?: string;
}
interface DataPoint {
  label: string;
  value: number;
  fill?: string;
}
function buildConfig(series: Series[]): ChartConfig {
  const config: ChartConfig = {};
  for (let i = 0; i < series.length; i++) {
    config[series[i].dataKey] = {
      label: series[i].label,
      color: series[i].color ?? CHART_COLORS[i % CHART_COLORS.length]
    };
  }
  return config;
}
function buildPieConfig(data: DataPoint[]): ChartConfig {
  const config: ChartConfig = {};
  for (let i = 0; i < data.length; i++) {
    config[data[i].label] = {
      label: data[i].label,
      color: data[i].fill ?? CHART_COLORS[i % CHART_COLORS.length]
    };
  }
  return config;
}
function ChartWrapper(t0) {
  const $ = _c(8);
  const {
    title,
    description,
    children
  } = t0;
  let t1;
  if ($[0] !== description || $[1] !== title) {
    t1 = (title || description) && <CardHeader>{title && <CardTitle>{title}</CardTitle>}{description && <CardDescription>{description}</CardDescription>}</CardHeader>;
    $[0] = description;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children) {
    t2 = <CardContent className="min-h-[300px]">{children}</CardContent>;
    $[3] = children;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <Card>{t1}{t2}</Card>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
export function BarChartComponent(t0) {
  const $ = _c(29);
  const {
    props
  } = t0;
  if (!props.data?.length || !props.series?.length) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex h-[300px] items-center justify-center text-muted-foreground">No data</div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== props.description || $[2] !== props.title) {
      t2 = <ChartWrapper title={props.title} description={props.description}>{t1}</ChartWrapper>;
      $[1] = props.description;
      $[2] = props.title;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== props.series) {
    t1 = buildConfig(props.series);
    $[4] = props.series;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const config = t1;
  const isVertical = props.layout === "vertical";
  const t2 = props.title;
  const t3 = props.description;
  const t4 = props.data;
  const t5 = isVertical ? "vertical" : "horizontal";
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <CartesianGrid vertical={false} />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== isVertical || $[8] !== props.xAxisKey) {
    t7 = isVertical ? <><YAxis dataKey={props.xAxisKey} type="category" tickLine={false} axisLine={false} /><XAxis type="number" hide={true} /></> : <XAxis dataKey={props.xAxisKey} tickLine={false} axisLine={false} />;
    $[7] = isVertical;
    $[8] = props.xAxisKey;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ChartTooltip content={<ChartTooltipContent />} />;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ChartLegend content={<ChartLegendContent />} />;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== props.series || $[13] !== props.stacked) {
    let t11;
    if ($[15] !== props.stacked) {
      t11 = (s, i) => <Bar key={s.dataKey} dataKey={s.dataKey} fill={s.color ?? CHART_COLORS[i % CHART_COLORS.length]} radius={4} stackId={props.stacked ? "stack" : s.stackId} />;
      $[15] = props.stacked;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    t10 = props.series.map(t11);
    $[12] = props.series;
    $[13] = props.stacked;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[17] !== props.data || $[18] !== t10 || $[19] !== t5 || $[20] !== t7) {
    t11 = <RechartsBarChart data={t4} layout={t5}>{t6}{t7}{t8}{t9}{t10}</RechartsBarChart>;
    $[17] = props.data;
    $[18] = t10;
    $[19] = t5;
    $[20] = t7;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== config || $[23] !== t11) {
    t12 = <ChartContainer config={config} className="h-[300px] w-full">{t11}</ChartContainer>;
    $[22] = config;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== props.description || $[26] !== props.title || $[27] !== t12) {
    t13 = <ChartWrapper title={t2} description={t3}>{t12}</ChartWrapper>;
    $[25] = props.description;
    $[26] = props.title;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  return t13;
}
export function LineChartComponent(t0) {
  const $ = _c(27);
  const {
    props
  } = t0;
  if (!props.data?.length || !props.series?.length) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex h-[300px] items-center justify-center text-muted-foreground">No data</div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== props.description || $[2] !== props.title) {
      t2 = <ChartWrapper title={props.title} description={props.description}>{t1}</ChartWrapper>;
      $[1] = props.description;
      $[2] = props.title;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== props.series) {
    t1 = buildConfig(props.series);
    $[4] = props.series;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const config = t1;
  const t2 = props.title;
  const t3 = props.description;
  const t4 = props.data;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <CartesianGrid vertical={false} />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== props.xAxisKey) {
    t6 = <XAxis dataKey={props.xAxisKey} tickLine={false} axisLine={false} />;
    $[7] = props.xAxisKey;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ChartTooltip content={<ChartTooltipContent />} />;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ChartLegend content={<ChartLegendContent />} />;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== props.curved || $[12] !== props.series) {
    let t10;
    if ($[14] !== props.curved) {
      t10 = (s, i) => <Line key={s.dataKey} type={props.curved ? "monotone" : "linear"} dataKey={s.dataKey} stroke={s.color ?? CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false} />;
      $[14] = props.curved;
      $[15] = t10;
    } else {
      t10 = $[15];
    }
    t9 = props.series.map(t10);
    $[11] = props.curved;
    $[12] = props.series;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[16] !== props.data || $[17] !== t6 || $[18] !== t9) {
    t10 = <RechartsLineChart data={t4}>{t5}{t6}{t7}{t8}{t9}</RechartsLineChart>;
    $[16] = props.data;
    $[17] = t6;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== config || $[21] !== t10) {
    t11 = <ChartContainer config={config} className="h-[300px] w-full">{t10}</ChartContainer>;
    $[20] = config;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== props.description || $[24] !== props.title || $[25] !== t11) {
    t12 = <ChartWrapper title={t2} description={t3}>{t11}</ChartWrapper>;
    $[23] = props.description;
    $[24] = props.title;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
}
export function AreaChartComponent(t0) {
  const $ = _c(32);
  const {
    props
  } = t0;
  if (!props.data?.length || !props.series?.length) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex h-[300px] items-center justify-center text-muted-foreground">No data</div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== props.description || $[2] !== props.title) {
      t2 = <ChartWrapper title={props.title} description={props.description}>{t1}</ChartWrapper>;
      $[1] = props.description;
      $[2] = props.title;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== props.series) {
    t1 = buildConfig(props.series);
    $[4] = props.series;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const config = t1;
  const t2 = props.title;
  const t3 = props.description;
  const t4 = props.data;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <CartesianGrid vertical={false} />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== props.xAxisKey) {
    t6 = <XAxis dataKey={props.xAxisKey} tickLine={false} axisLine={false} />;
    $[7] = props.xAxisKey;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ChartTooltip content={<ChartTooltipContent />} />;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ChartLegend content={<ChartLegendContent />} />;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== props.series) {
    t9 = props.series.map(_temp);
    $[11] = props.series;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t9) {
    t10 = <defs>{t9}</defs>;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== props.series || $[16] !== props.stacked) {
    let t12;
    if ($[18] !== props.stacked) {
      t12 = (s_0, i_0) => <Area key={s_0.dataKey} type="monotone" dataKey={s_0.dataKey} stroke={s_0.color ?? CHART_COLORS[i_0 % CHART_COLORS.length]} fill={`url(#fill-${s_0.dataKey})`} stackId={props.stacked ? "stack" : undefined} />;
      $[18] = props.stacked;
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    t11 = props.series.map(t12);
    $[15] = props.series;
    $[16] = props.stacked;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[20] !== props.data || $[21] !== t10 || $[22] !== t11 || $[23] !== t6) {
    t12 = <RechartsAreaChart data={t4}>{t5}{t6}{t7}{t8}{t10}{t11}</RechartsAreaChart>;
    $[20] = props.data;
    $[21] = t10;
    $[22] = t11;
    $[23] = t6;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== config || $[26] !== t12) {
    t13 = <ChartContainer config={config} className="h-[300px] w-full">{t12}</ChartContainer>;
    $[25] = config;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== props.description || $[29] !== props.title || $[30] !== t13) {
    t14 = <ChartWrapper title={t2} description={t3}>{t13}</ChartWrapper>;
    $[28] = props.description;
    $[29] = props.title;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
}
function _temp(s, i) {
  return <linearGradient key={s.dataKey} id={`fill-${s.dataKey}`} x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={s.color ?? CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0.8} /><stop offset="95%" stopColor={s.color ?? CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0.1} /></linearGradient>;
}
export function PieChartComponent(t0) {
  const $ = _c(25);
  const {
    props
  } = t0;
  let t1;
  if ($[0] !== props.data) {
    t1 = props.data.map(_temp2);
    $[0] = props.data;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const chartData = t1;
  let t2;
  if ($[2] !== props.data) {
    t2 = buildPieConfig(props.data);
    $[2] = props.data;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const config = t2;
  let t3;
  if ($[4] !== props.data) {
    t3 = props.data.reduce(_temp3, 0);
    $[4] = props.data;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const total = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ChartTooltip content={<ChartTooltipContent hideLabel={true} />} />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = props.innerRadius ?? 0;
  let t6;
  if ($[7] !== props.innerRadius || $[8] !== total) {
    t6 = props.innerRadius ? <Label content={t7 => {
      const {
        viewBox
      } = t7;
      if (viewBox && "cx" in viewBox && "cy" in viewBox) {
        return <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle"><tspan x={viewBox.cx} y={viewBox.cy} fill="currentColor" style={{
            fontSize: "1.875rem",
            fontWeight: 700
          }}>{total.toLocaleString()}</tspan><tspan x={viewBox.cx} y={(viewBox.cy ?? 0) + 24} fill="currentColor" fillOpacity={0.6}>Total</tspan></text>;
      }
      return null;
    }} /> : null;
    $[7] = props.innerRadius;
    $[8] = total;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== chartData || $[11] !== props.showLabel || $[12] !== t5 || $[13] !== t6) {
    t7 = <Pie data={chartData} dataKey="value" nameKey="label" innerRadius={t5} label={props.showLabel}>{t6}</Pie>;
    $[10] = chartData;
    $[11] = props.showLabel;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ChartLegend content={<ChartLegendContent nameKey="label" />} />;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t7) {
    t9 = <RechartsPieChart>{t4}{t7}{t8}</RechartsPieChart>;
    $[16] = t7;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== config || $[19] !== t9) {
    t10 = <ChartContainer config={config} className="mx-auto h-[300px] w-[300px]">{t9}</ChartContainer>;
    $[18] = config;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== props.description || $[22] !== props.title || $[23] !== t10) {
    t11 = <ChartWrapper title={props.title} description={props.description}>{t10}</ChartWrapper>;
    $[21] = props.description;
    $[22] = props.title;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
}
function _temp3(sum, d_0) {
  return sum + d_0.value;
}
function _temp2(d, i) {
  return {
    ...d,
    fill: d.fill ?? CHART_COLORS[i % CHART_COLORS.length]
  };
}
export function RadarChartComponent(t0) {
  const $ = _c(24);
  const {
    props
  } = t0;
  if (!props.data?.length || !props.series?.length) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex h-[300px] items-center justify-center text-muted-foreground">No data</div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== props.description || $[2] !== props.title) {
      t2 = <ChartWrapper title={props.title} description={props.description}>{t1}</ChartWrapper>;
      $[1] = props.description;
      $[2] = props.title;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== props.series) {
    t1 = buildConfig(props.series);
    $[4] = props.series;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const config = t1;
  const t2 = props.title;
  const t3 = props.description;
  const t4 = props.data;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <PolarGrid />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== props.axisKey) {
    t6 = <PolarAngleAxis dataKey={props.axisKey} />;
    $[7] = props.axisKey;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <PolarRadiusAxis />;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ChartTooltip content={<ChartTooltipContent />} />;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== props.series) {
    t9 = props.series.map(_temp4);
    $[11] = props.series;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== props.data || $[14] !== t6 || $[15] !== t9) {
    t10 = <RechartsRadarChart data={t4}>{t5}{t6}{t7}{t8}{t9}</RechartsRadarChart>;
    $[13] = props.data;
    $[14] = t6;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== config || $[18] !== t10) {
    t11 = <ChartContainer config={config} className="mx-auto h-[300px] w-[300px]">{t10}</ChartContainer>;
    $[17] = config;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== props.description || $[21] !== props.title || $[22] !== t11) {
    t12 = <ChartWrapper title={t2} description={t3}>{t11}</ChartWrapper>;
    $[20] = props.description;
    $[21] = props.title;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
}
function _temp4(s, i) {
  return <Radar key={s.dataKey} dataKey={s.dataKey} fill={s.color ?? CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.3} stroke={s.color ?? CHART_COLORS[i % CHART_COLORS.length]} />;
}
export function RadialBarChartComponent(t0) {
  const $ = _c(25);
  const {
    props
  } = t0;
  if (!props.data?.length) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex h-[300px] items-center justify-center text-muted-foreground">No data</div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== props.description || $[2] !== props.title) {
      t2 = <ChartWrapper title={props.title} description={props.description}>{t1}</ChartWrapper>;
      $[1] = props.description;
      $[2] = props.title;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== props.data) {
    t1 = props.data.map(_temp5);
    $[4] = props.data;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const chartData = t1;
  let t2;
  if ($[6] !== props.data) {
    t2 = buildPieConfig(props.data);
    $[6] = props.data;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const config = t2;
  const t3 = props.innerRadius ?? 30;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ChartTooltip content={<ChartTooltipContent hideLabel={true} />} />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== props.showLabel) {
    t5 = props.showLabel ? {
      position: "insideStart"
    } : undefined;
    $[9] = props.showLabel;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <RadialBar dataKey="value" background={true} label={t5} />;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ChartLegend content={<ChartLegendContent nameKey="label" />} />;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== chartData || $[15] !== t3 || $[16] !== t6) {
    t8 = <RechartsRadialBarChart data={chartData} innerRadius={t3} outerRadius={110}>{t4}{t6}{t7}</RechartsRadialBarChart>;
    $[14] = chartData;
    $[15] = t3;
    $[16] = t6;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== config || $[19] !== t8) {
    t9 = <ChartContainer config={config} className="mx-auto h-[300px] w-[300px]">{t8}</ChartContainer>;
    $[18] = config;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== props.description || $[22] !== props.title || $[23] !== t9) {
    t10 = <ChartWrapper title={props.title} description={props.description}>{t9}</ChartWrapper>;
    $[21] = props.description;
    $[22] = props.title;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
}
function _temp5(d, i) {
  return {
    ...d,
    fill: d.fill ?? CHART_COLORS[i % CHART_COLORS.length]
  };
}